import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 监听各 section 进入视口，更新高亮
  useEffect(() => {
    const sectionIds = ['home', 'about', 'services', 'projects', 'testimonials', 'news'];
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id);
          }
        },
        { threshold: 0.3, rootMargin: '-80px 0px 0px 0px' }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { name: 'ホーム', href: '#home', id: 'home' },
    { name: '会社概要', href: '#about', id: 'about' },
    { name: 'サービス', href: '#services', id: 'services' },
    { name: '施工実績', href: '#projects', id: 'projects' },
    { name: 'お客様の声', href: '#testimonials', id: 'testimonials' },
    { name: 'ニュース', href: '#news', id: 'news' },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (href.startsWith('#')) {
      if (location.pathname === '/') {
        const targetId = href.replace('#', '');
        const element = document.getElementById(targetId);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      } else {
        navigate('/' + href);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto px-4 sm:px-8 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={(e) => handleNavClick(e as unknown as React.MouseEvent<HTMLButtonElement>, '#home')}
              className="flex items-center gap-2 sm:gap-3 cursor-pointer bg-transparent border-none p-0"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/23997eba-51d5-4500-9256-9f0c6c6cd3b5.png"
                alt="株式会社永恒"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain rounded-full"
              />
              <div>
                <h1
                  className={`text-base sm:text-xl font-bold tracking-widest leading-none ${
                    isScrolled ? 'text-[#1A1A1A]' : 'text-white'
                  }`}
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  株式会社永恒
                </h1>
                <p className={`text-[10px] tracking-widest mt-0.5 ${isScrolled ? 'text-[#B8960C]' : 'text-[#D4AF37]'}`}>
                  EIKOU CORPORATION
                </p>
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={`relative text-base font-medium tracking-wider transition-colors cursor-pointer whitespace-nowrap group ${
                      isActive
                        ? 'text-[#B8960C]'
                        : isScrolled
                        ? 'text-[#333] hover:text-[#B8960C]'
                        : 'text-white/90 hover:text-[#D4AF37]'
                    }`}
                  >
                    {item.name}
                    {/* 高亮下划线 */}
                    <span
                      className={`absolute -bottom-1 left-0 h-0.5 bg-[#B8960C] transition-all duration-300 ${
                        isActive ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}
                    />
                  </a>
                );
              })}
            </div>

            {/* CTA Button - Desktop */}
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, '#contact')}
              className="hidden lg:inline-flex items-center gap-2 bg-[#B8960C] text-white px-5 py-2.5 text-base font-medium tracking-wider hover:bg-[#9A7D0A] transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line"></i>
              </div>
              無料相談
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors cursor-pointer border-none bg-transparent ${
                isScrolled ? 'text-[#1A1A1A]' : 'text-white'
              }`}
              aria-label="メニュー"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex flex-col">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="relative mt-[64px] bg-white shadow-xl border-t border-gray-100 z-50">
            <div className="px-6 py-6 flex flex-col gap-1">
              {navItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={`block px-4 py-3 text-sm font-medium tracking-wider border-b border-gray-100 transition-colors cursor-pointer rounded-sm ${
                      isActive
                        ? 'text-[#B8960C] bg-[#FBF8F0] border-l-2 border-l-[#B8960C]'
                        : 'text-[#333] hover:text-[#B8960C] hover:bg-[#FBF8F0]'
                    }`}
                  >
                    {item.name}
                  </a>
                );
              })}
              <a
                href="#contact"
                onClick={(e) => handleNavClick(e, '#contact')}
                className="mt-3 block bg-[#B8960C] text-white px-6 py-3 text-sm font-medium tracking-wider text-center hover:bg-[#9A7D0A] transition-colors whitespace-nowrap cursor-pointer"
              >
                無料相談
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
